import { FC, MouseEvent, useEffect, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import styles from "@/components/appointmentArchive/appointmentArchiveTable.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import DateRangeFilterModal, {
  type DateRangeFilterModalValue,
} from "@/components/dateTime/dateRangeFilterModal";
import DateRangeFilterChip from "@/components/dateTime/dateRangeFilterChip";
import { RootState } from "@/components/state/store";
import { setTableSearch } from "@/components/state/tableSearch/tableSearchSlice";

type AppointmentSubjectType = "worker" | "company";

const TABLE_ENTITY = "appointment/worker_appointments";

const ManagerAppointmentArchiveTable: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const login = useSelector((state: RootState) => state.login);
  const selectedCompany = useSelector((state: RootState) => state.parent);
  const selectedCompanySubjectId = String(
    selectedCompany.subjectId || login.subjectId,
  );
  const isDepartmentManagedCompany = login.department_manager_for.includes(
    selectedCompanySubjectId,
  );
  const [appointmentSubjectType, setAppointmentSubjectType] =
    useState<AppointmentSubjectType>("worker");
  const effectiveAppointmentSubjectType = isDepartmentManagedCompany
    ? "worker"
    : appointmentSubjectType;

  usePageHeading(t("appointment_archive"));

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const statusFilterOptions = getTranslatedStatusOptions("workerAppointments");
  const columns = [
    {
      header: t("id"),
      id: "id",
      accessorKey: "id",
      enableHiding: true,
      visibleInShowHideMenu: false,
    },
    ...(effectiveAppointmentSubjectType === "worker"
      ? [
          {
            header: t("customer"),
            accessorKey: "subject.name",
            childEntityName: "subject",
            id: "subject__name",
            customColumnFilterId: "subject_name",
            customSortingKey: "subject_name",
            accessorFn: (row: any) => row.subject?.name,
          },
        ]
      : []),
    {
      header: t("building_location"),
      accessorKey: "building_information.city",
      id: "building_information__name",
      customColumnFilterId: "building_information__building__name",
      customSortingKey: "building_name",
      childEntityName: "building_information",
      accessorFn: (row: any) => {
        const parts = [];
        const { name, street_house, zip, city } = row.building_information;
        if (name) {
          parts.push(name);
        }
        if (street_house) {
          parts.push(street_house);
        }
        if (zip || city) {
          const zipCity = [zip, city].filter(Boolean).join(" ");
          parts.push(zipCity);
        }
        return parts.join(", ");
      },
    },
    {
      header: t("department"),
      accessorKey: "subject.department_set",
      id: "departmentName",
      customColumnFilterId: "department_name",
      customSortingKey: "department_set__name",
      accessorFn: (row: any) => {
        const departmentSet = row.subject?.department_set;
        const departments = [];
        for (const key in departmentSet) {
          departments.push(departmentSet[key].name);
        }
        return departments.join(", ");
      },
    },
    {
      header: t("examination"),
      accessorKey: "examination_set",
      id: "examination_set",
      customColumnFilterId: "examination_set__name",
      customSortingKey: "examination_name",
      accessorFn: (row: any) => {
        const examinations = [];
        if (row.examination_set) {
          for (const key in row.examination_set) {
            if (row.examination_set[key].name) {
              examinations.push(row.examination_set[key].name);
            }
          }
        }
        return examinations.join(", ");
      },
    },
    {
      header: t("date"),
      accessorKey: "datetime",
      id: "datetime",
      filterVariant: "date",
      sortingFn: "date",
      accessorFn: (row: any) => {
        if (!row.datetime) {
          return "-";
        }
        return `${dayjs(row.datetime).format("DD.MM.YYYY")} - ${dayjs(
          row.datetime,
        ).format("HH:mm")} ${t("time_indicator")} `;
      },
    },
    {
      header: t("status"),
      accessorKey: "status",
      filterVariant: "select",
      filterSelectOptions: statusFilterOptions,
      id: "status",
      accessorFn: (row: any) => t(`status_${row.status}`),
    },
  ];

  const additionalParametersForFilter: Record<string, boolean | string[]> = {
    company_appointments: effectiveAppointmentSubjectType === "company",
  };
  if (startDate !== null && endDate !== null) {
    additionalParametersForFilter.datetime_date_range = [
      dayjs(startDate).format("YYYY-MM-DD"),
      dayjs(endDate).format("YYYY-MM-DD"),
    ];
  }

  useEffect(() => {
    if (!isDepartmentManagedCompany || appointmentSubjectType === "worker") {
      return;
    }
    dispatch(setTableSearch({ entity: TABLE_ENTITY, searchValue: "" }));
    setAppointmentSubjectType("worker");
  }, [appointmentSubjectType, dispatch, isDepartmentManagedCompany]);

  const updateDateRangeFilter = (dateRange: DateRangeFilterModalValue) => {
    const [nextStartDate, nextEndDate] = dateRange;
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
  };

  const applyDateRange = (dateRange: DateRangeFilterModalValue) => {
    updateDateRangeFilter(dateRange);
    setModalOpen(false);
  };

  const clearDateRangeFilter = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleClearDate = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    clearDateRangeFilter();
  };

  const navigateDateRange = (direction: "left" | "right") => {
    if (startDate === null || endDate === null) return;

    const rangeLengthInDays = dayjs(endDate).diff(dayjs(startDate), "day") + 1;
    const offset =
      direction === "left" ? -rangeLengthInDays : rangeLengthInDays;

    updateDateRangeFilter([
      dayjs(startDate).add(offset, "day").toDate(),
      dayjs(endDate).add(offset, "day").toDate(),
    ]);
  };

  const handleAppointmentSubjectTypeChange = (
    _event: MouseEvent<HTMLElement>,
    nextAppointmentSubjectType: AppointmentSubjectType | null,
  ) => {
    if (nextAppointmentSubjectType === null) return;

    dispatch(setTableSearch({ entity: TABLE_ENTITY, searchValue: "" }));
    setAppointmentSubjectType(nextAppointmentSubjectType);
  };

  const customToolbarButton = () => (
    <div className={styles.toolbarButtonContainer}>
      {!isDepartmentManagedCompany && (
        <ToggleButtonGroup
          value={effectiveAppointmentSubjectType}
          exclusive
          size="small"
          onChange={handleAppointmentSubjectTypeChange}
          aria-label={t("appointment_archive")}
        >
          <ToggleButton value="worker">{t("customer")}</ToggleButton>
          <ToggleButton value="company">{t("company")}</ToggleButton>
        </ToggleButtonGroup>
      )}

      <GenericButton onClick={() => setModalOpen(true)}>
        {t("period")}
      </GenericButton>

      {startDate !== null && endDate !== null && (
        <DateRangeFilterChip
          filterLabel={t("date")}
          startDate={startDate}
          endDate={endDate}
          onDelete={handleClearDate}
          onClick={() => setModalOpen(true)}
          onNavigateLeft={() => navigateDateRange("left")}
          onNavigateRight={() => navigateDateRange("right")}
        />
      )}
    </div>
  );

  return (
    <>
      <GenericCrudTable
        key={effectiveAppointmentSubjectType}
        entity={TABLE_ENTITY}
        columns={columns}
        showRowActions={false}
        additionalUrlParameter={additionalParametersForFilter}
        customTopToolBarAction={customToolbarButton}
        hasAdditionalFiltersToReset={startDate !== null || endDate !== null}
        onResetAllFilters={clearDateRangeFilter}
        disableAddItem={true}
        showColumnFilterbyDefault={true}
      />
      {modalOpen && (
        <DateRangeFilterModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={t("choose_period")}
          initialDateRange={[startDate, endDate]}
          onApply={applyDateRange}
        />
      )}
    </>
  );
};

export default ManagerAppointmentArchiveTable;
